import type { Extra, ShopItem } from '../types'
import { makeTranslator, normalizeLocale, type Translate } from '../i18n/core'

export function describeShopItem(item: ShopItem, locale?: string, t: Translate = makeTranslator(locale || 'de-DE')) {
    const subscription = item.subType === 'subscription' || !!item.subscription
    const metadata = item.metaData && !Array.isArray(item.metaData) ? item.metaData : {}
    const total = subscription && Number.isFinite(Number(metadata.total)) ? Number(metadata.total) : item.amount
    const joker = /joker/i.test(item.type ?? '') || item.theme === 'joker'
    const period = item.subscription?.payIntervalUnit ?? ''
    const quantity = item.subscription?.payIntervalAmount ?? 1
    const periods: Record<string, [string, string]> = { day: ['Tag', 'Tage'], week: ['Woche', 'Wochen'], month: ['Monat', 'Monate'], year: ['Jahr', 'Jahre'] }
    const interval = !period ? '' : quantity === 1 ? `/ ${t(periods[period]?.[0] ?? period)}` : `/ ${quantity} ${t(periods[period]?.[1] ?? period)}`
    return {
        amount: formatShopAmount(total, locale), unit: t(item.type === 'ultimate-pass' ? 'Ultimate Pass' : joker ? 'Joker' : 'Chips'), joker, subscription,
        title: subscription ? item.title : undefined,
        perDay: subscription && metadata.perDay != null ? `${formatShopAmount(Number(metadata.perDay), locale)} / ${t('Tag')}` : undefined,
        interval,
    }
}
export function formatShopAmount(amount: number, locale?: string): string {
    if (!Number.isFinite(amount)) return '0'
    // Match the web shop: compact labels must never round up to chips the user does not receive.
    const unit = amount >= 1e15 ? 1e15 : amount >= 1e12 ? 1e12 : amount >= 1e9 ? 1e9 : 1e6
    const step = amount >= 1e9 ? unit / 100 : unit
    const displayAmount = amount >= 1e6 ? Math.floor(amount / step) * step : amount
    if (locale) return new Intl.NumberFormat(normalizeLocale(locale), { notation: amount >= 1e6 ? 'compact' : 'standard', maximumFractionDigits: amount >= 1e9 ? 2 : 0 }).format(displayAmount)
    // Compatibility for consumers outside the localized application.
    if (amount >= 1e9) return `${(Math.floor(amount / 1e7) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} BN`
    if (amount >= 1e6) return `${Math.floor(amount / 1e6).toLocaleString('de-DE', { maximumFractionDigits: 0 })} MM`
    return amount.toLocaleString('de-DE')
}
export function formatShopPrice(item: ShopItem, price = item.price, locale?: string): string {
    if (!price) return makeTranslator(locale || 'de-DE')('Preis nicht verfügbar')
    const currency = item.currency || 'EUR'
    if (locale && /^\d+(?:\.\d+)?$/.test(price)) {
        try { return new Intl.NumberFormat(normalizeLocale(locale), { style: 'currency', currency }).format(Number(price)) } catch { return `${new Intl.NumberFormat(normalizeLocale(locale)).format(Number(price))} ${currency}` }
    }
    // Non-numeric values are already localized by the backend. Do not parse ambiguous separators.
    const symbol = currency === 'EUR' ? '€' : currency
    const normalized = !locale && /^\d+\.\d{1,2}$/.test(price) ? price.replace('.', ',') : price
    return (item.priceFormat || '{{price}} {{currency}}').replace('{{price}}', normalized).replace('{{currency}}', symbol)
}
export function formatShopExtraAmount(extra: Pick<Extra, 'amount' | 'amountText'>, locale?: string): string {
    const amount = extra.amountText?.trim() || formatShopAmount(extra.amount, locale)
    return `+${amount.replace(/^\+\s*/, '')}`
}

/** Keep server-translated reward names, removing only a matching leading quantity. */
export function formatShopExtraPresentation(extra: Extra, locale?: string) {
    const label = extra.title || extra.goodName
    const prefix = label.match(/^\+?([\d.,\s\u00a0\u202f]+)\s+(.+)$/u)
    const numeric = prefix?.[1].replace(/[^0-9]/g, '')
    if (prefix && numeric === String(extra.amount)) {
        return { amount: `+${new Intl.NumberFormat(normalizeLocale(locale || 'de-DE')).format(extra.amount)}`, label: prefix[2] }
    }
    return { amount: formatShopExtraAmount(extra, locale), label }
}
